<template>
	<div class="price-list-cell">
		<SuiAmountInput
			class="price-list-cell__input"
			:model-value="sellingPriceValue"
			:formatted-value="sellingPriceFormattedValue"
			hide-clear-button
			:variant="
				v.sellingPrice.$error
					? hasSaveAttempt
						? 'error'
						: 'warning'
					: undefined
			"
			:message="v.sellingPrice.$errors[0]?.$message"
			size="small"
			@update:model-value="onInput"
			@temporary-input="onTemporaryInput"
			@blur="onBlur"
		/>
		<span class="price-list-cell__currency"> {{ currency }} </span>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed } from 'vue';
import { SuiAmountInput } from '@developers/scoro-ui';
import { useVuelidate } from '@vuelidate/core';
import { nonNegativeNumber, withI18nMessage } from '@/utils/validators';
import { useFormatter } from '@/composables';
import { parseUserInputNumberNeverNaN } from '@/utils/parseUserInputNumber';
import { wait } from '@/utils/misc';

const nonNegativeSellingPriceValidator = withI18nMessage(nonNegativeNumber, {
	messagePath: () => 'role_selling_price_validation_non_negative',
});

export default defineComponent({
	components: {
		SuiAmountInput,
	},
	props: {
		sellingPrice: {
			type: Number as PropType<number | undefined>,
			required: false,
			default: undefined,
		},
		hasSaveAttempt: {
			type: Boolean,
			required: false,
			default: false,
		},
		currency: {
			type: String,
			required: true,
		},
	},
	emits: ['input'],
	setup(props, { emit }) {
		const { formatNumber } = useFormatter();

		const sellingPriceValue = computed(() => {
			return typeof props.sellingPrice === 'number'
				? String(props.sellingPrice)
				: '';
		});

		const sellingPriceFormattedValue = computed(() => {
			return typeof props.sellingPrice === 'number'
				? formatNumber(props.sellingPrice, {
						minimumFractionDigits: 0,
						maximumFractionDigits: 20,
					})
				: '';
		});

		const v = useVuelidate(
			{ sellingPrice: { nonNegativeSellingPriceValidator } },
			{ sellingPrice: sellingPriceValue },
			{ $rewardEarly: true }
		);

		const onInput = (value: string) => {
			const parsedValue = parseUserInputNumberNeverNaN(value.trim());
			if (parsedValue === props.sellingPrice) {
				return;
			}

			emit('input', parsedValue);
		};

		const onTemporaryInput = (value: string) => {
			const parsed = parseUserInputNumberNeverNaN(value.trim());
			if (parsed === undefined) {
				return;
			}

			emit('input', parsed);

			v.value.sellingPrice.$touch();
		};

		const onBlur = async () => {
			// If we run `$commit()` instantly, the input can fail to show the
			// error for some reason (happens only some times). `$errors` are
			// not populated even though the touched and commit are both true.
			// Defer the commit a little bit to always show errors.
			await wait();

			v.value.sellingPrice.$commit();
		};
		return {
			sellingPriceValue,
			sellingPriceFormattedValue,
			v,
			onInput,
			onTemporaryInput,
			onBlur,
		};
	},
});
</script>

<style lang="scss">
.price-list-cell {
	display: flex;
	align-items: center;

	&__input {
		text-align: end;
		width: 10rem;
	}

	&__currency {
		margin-left: 1rem;
		color: $neutral-70;
		font-size: 1.5rem;
	}
}
</style>
